import { useState, useCallback, useEffect } from 'react';
import {
  AlertTriangle,
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lightbulb,
  Loader2,
  RefreshCw,
  Search,
  Target,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { useToast } from '../../contexts/ToastContext';
import type {
  MetaInterest,
  MetaSavedInterest,
  MetaSearchHistoryItem,
  MetaTargetingSearchResult,
  MetaTargetingStatus,
} from '../../types';
import * as metaTargetingService from '../../services/meta-targeting.service';

interface MetaTargetingPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  isAdmin: boolean;
}

const PAGE_SIZE = 20;

const LOCALE_OPTIONS = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es_ES', label: 'Español (España)' },
];

function formatAudienceNumber(size: number): string {
  if (size >= 1_000_000_000) return `${(size / 1_000_000_000).toFixed(1)}B`;
  if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(1)}M`;
  if (size >= 1_000) return `${(size / 1_000).toFixed(0)}K`;
  return size.toLocaleString('pt-BR');
}

function formatAudience(interest: { audienceSize: number | null; audienceSizeLowerBound?: number; audienceSizeUpperBound?: number }): string {
  if (interest.audienceSizeLowerBound && interest.audienceSizeUpperBound) {
    return `${formatAudienceNumber(interest.audienceSizeLowerBound)} – ${formatAudienceNumber(interest.audienceSizeUpperBound)}`;
  }
  if (interest.audienceSize) return formatAudienceNumber(interest.audienceSize);
  return '—';
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MetaTargetingPage({
  accessToken,
  refreshToken,
  attemptRefresh,
  isAdmin,
}: MetaTargetingPageProps) {
  const toast = useToast();

  // API status
  const [status, setStatus] = useState<MetaTargetingStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Search tab state
  const [searchQuery, setSearchQuery] = useState('');
  const [locale, setLocale] = useState('pt_BR');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<MetaTargetingSearchResult | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [cacheInvalidating, setCacheInvalidating] = useState(false);

  // Saved interests tab state
  const [savedInterests, setSavedInterests] = useState<MetaSavedInterest[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedPage, setSavedPage] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [editingInterest, setEditingInterest] = useState<MetaSavedInterest | null>(null);
  const [notesValue, setNotesValue] = useState('');

  // History tab state
  const [historyItems, setHistoryItems] = useState<MetaSearchHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyKeyword, setHistoryKeyword] = useState('');
  const [historySearchInput, setHistorySearchInput] = useState('');

  const getToken = useCallback(async (): Promise<string | null> => {
    if (accessToken) return accessToken;
    if (refreshToken) {
      const refreshed = await attemptRefresh();
      if (refreshed && accessToken) return accessToken;
    }
    return null;
  }, [accessToken, refreshToken, attemptRefresh]);

  // Fetch API status on mount
  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await metaTargetingService.fetchMetaTargetingStatus(token);
      setStatus(result);
    } catch (err) {
      // Status errors are non-blocking — show as toast
      toast.error(err instanceof Error ? err.message : 'Erro ao verificar status');
    } finally {
      setStatusLoading(false);
    }
  }, [getToken, toast]);

  useEffect(() => {
    void fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search
  const handleSearch = useCallback(async (keyword?: string, localeOverride?: string) => {
    const q = (keyword ?? searchQuery).trim();
    if (!q) return;

    const token = await getToken();
    if (!token) return;

    setSearchLoading(true);
    setSearchError(null);
    setSavedIds(new Set());

    try {
      const result = await metaTargetingService.searchMetaTargeting(q, localeOverride ?? locale, token);
      setSearchResult(result);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Erro ao pesquisar interesses');
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, locale, getToken]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSearch();
    }
  }, [handleSearch]);

  // Suggestions
  const handleGetSuggestions = useCallback(async (interest: MetaInterest) => {
    const token = await getToken();
    if (!token) return;

    setSuggestionsLoading(true);
    setSearchError(null);

    try {
      const result = await metaTargetingService.fetchMetaTargetingSuggestions([interest.name], token);
      setSearchResult(result);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Erro ao buscar sugestões');
    } finally {
      setSuggestionsLoading(false);
    }
  }, [getToken]);

  // Save interest
  const handleSaveInterest = useCallback(async (interest: MetaInterest) => {
    if (!searchResult) return;
    const token = await getToken();
    if (!token) return;

    setSavingIds(prev => new Set(prev).add(interest.id));

    try {
      await metaTargetingService.saveMetaInterest(
        {
          metaInterestId: interest.id,
          name: interest.name,
          audienceSize: interest.audienceSize,
          path: interest.path,
          description: interest.description,
          topic: interest.topic,
          keywordSearched: searchResult.keyword,
        },
        token
      );
      setSavedIds(prev => new Set(prev).add(interest.id));
      toast.success(`Interesse "${interest.name}" salvo com sucesso.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar interesse');
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(interest.id);
        return next;
      });
    }
  }, [searchResult, getToken, toast]);

  // Cache invalidate (admin only)
  const handleInvalidateCache = useCallback(async () => {
    if (!searchResult?.keyword) return;
    const token = await getToken();
    if (!token) return;

    setCacheInvalidating(true);
    try {
      await metaTargetingService.invalidateMetaTargetingCache(searchResult.keyword, token);
      toast.success('Cache invalidado. Execute a pesquisa novamente para obter dados frescos.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao invalidar cache');
    } finally {
      setCacheInvalidating(false);
    }
  }, [searchResult, getToken, toast]);

  // Fetch saved interests
  const fetchSavedInterests = useCallback(async (page: number) => {
    const token = await getToken();
    if (!token) return;

    setSavedLoading(true);
    try {
      const result = await metaTargetingService.fetchSavedMetaInterests(page, PAGE_SIZE, token);
      setSavedInterests(result.items ?? []);
      setSavedTotal(result.total ?? 0);
      setSavedPage(result.page ?? 0);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar interesses salvos');
    } finally {
      setSavedLoading(false);
    }
  }, [getToken, toast]);

  // Delete saved interest
  const handleDeleteSaved = useCallback(async (id: string) => {
    const token = await getToken();
    if (!token) return;

    setDeletingId(id);
    try {
      await metaTargetingService.deleteMetaSavedInterest(id, token);
      toast.success('Interesse removido com sucesso.');
      await fetchSavedInterests(savedPage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir interesse');
    } finally {
      setDeletingId(null);
    }
  }, [getToken, toast, fetchSavedInterests, savedPage]);

  // Open notes dialog
  const openNotesDialog = useCallback((interest: MetaSavedInterest) => {
    setEditingInterest(interest);
    setNotesValue(interest.notes ?? '');
    setNotesDialogOpen(true);
  }, []);

  // Fetch history
  const fetchHistory = useCallback(async (page: number, keyword: string) => {
    const token = await getToken();
    if (!token) return;

    setHistoryLoading(true);
    try {
      const result = await metaTargetingService.fetchMetaTargetingHistory(keyword, page, PAGE_SIZE, token);
      setHistoryItems(result.items ?? []);
      setHistoryTotal(result.total ?? 0);
      setHistoryPage(result.page ?? 0);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar histórico');
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken, toast]);

  // Re-run search from history
  const handleRerunHistorySearch = useCallback((item: MetaSearchHistoryItem) => {
    setSearchQuery(item.keyword);
    void handleSearch(item.keyword, locale);
  }, [handleSearch, locale]);

  const savedTotalPages = Math.ceil(savedTotal / PAGE_SIZE);
  const historyTotalPages = Math.ceil(historyTotal / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-500/10">
          <Target className="h-6 w-6 text-blue-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Meta Targeting Research</h1>
          <p className="text-sm text-muted-foreground">
            Pesquise e salve interesses da Meta Marketing API para campanhas de anúncios
          </p>
        </div>
      </div>

      {/* Status Banner */}
      {statusLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Verificando configuração da API Meta...
        </div>
      ) : status && (!status.configured || !status.enabled) ? (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-yellow-700 dark:text-yellow-400">
              {!status.configured ? 'API Meta não configurada' : 'API Meta desativada'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {status.message || 'Configure o token de acesso da Meta Marketing API nas configurações do sistema para usar esta funcionalidade.'}
            </p>
          </div>
        </div>
      ) : status ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 bg-green-500/5">
          <div className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
          <p className="text-sm text-muted-foreground">
            {status.message || 'API Meta configurada e ativa'}
            {status.graphApiVersion && (
              <span className="ml-2 text-xs">({status.graphApiVersion})</span>
            )}
          </p>
        </div>
      ) : null}

      {/* Main Tabs */}
      <Tabs defaultValue="search" className="space-y-4">
        <TabsList>
          <TabsTrigger value="search" className="gap-2">
            <Search className="h-4 w-4" />
            Pesquisar Interesses
          </TabsTrigger>
          <TabsTrigger
            value="saved"
            className="gap-2"
            onClick={() => void fetchSavedInterests(0)}
          >
            <Bookmark className="h-4 w-4" />
            Interesses Salvos
            {savedTotal > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs h-4 px-1">
                {savedTotal}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="gap-2"
            onClick={() => void fetchHistory(0, '')}
          >
            <Clock className="h-4 w-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* === SEARCH TAB === */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Pesquisar Interesses</CardTitle>
              <CardDescription>
                Busque interesses para segmentação de anúncios no Meta Ads
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Label htmlFor="search-query" className="sr-only">Palavra-chave</Label>
                  <Input
                    id="search-query"
                    placeholder="Ex: acrilico, decoração, artesanato..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={searchLoading || suggestionsLoading}
                  />
                </div>
                <div className="w-full sm:w-52">
                  <Label htmlFor="locale-select" className="sr-only">Idioma</Label>
                  <Select value={locale} onValueChange={setLocale}>
                    <SelectTrigger id="locale-select">
                      <SelectValue placeholder="Idioma" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCALE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => void handleSearch()}
                  disabled={!searchQuery.trim() || searchLoading || suggestionsLoading}
                >
                  {searchLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Pesquisar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Error */}
          {searchError && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500/50 bg-red-500/10 text-sm text-red-600 dark:text-red-400">
              <XCircle className="h-4 w-4 shrink-0" />
              {searchError}
            </div>
          )}

          {/* Search Results */}
          {searchResult && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">
                      Resultados para &ldquo;{searchResult.keyword}&rdquo;
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">
                      {searchResult.resultCount} resultado{searchResult.resultCount !== 1 ? 's' : ''}
                    </Badge>
                    {searchResult.fromCache && (
                      <Badge variant="secondary" className="text-xs">
                        Cache
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && searchResult.fromCache && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void handleInvalidateCache()}
                        disabled={cacheInvalidating}
                        title="Invalidar cache e buscar dados frescos (apenas admin)"
                      >
                        {cacheInvalidating ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        )}
                        Invalidar Cache
                      </Button>
                    )}
                  </div>
                </div>
                {searchResult.cachedAt && (
                  <p className="text-xs text-muted-foreground">
                    Dados em cache de {formatDate(searchResult.cachedAt)}
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {searchResult.interests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Search className="h-8 w-8 mb-3 opacity-40" />
                    <p className="text-sm">Nenhum interesse encontrado para esta pesquisa.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead className="text-right">
                            <span className="flex items-center justify-end gap-1">
                              <Users className="h-3.5 w-3.5" />
                              Audiência
                            </span>
                          </TableHead>
                          <TableHead className="hidden md:table-cell">Tópico</TableHead>
                          <TableHead className="hidden lg:table-cell">Caminho</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResult.interests.map(interest => {
                          const isSaving = savingIds.has(interest.id);
                          const isSaved = savedIds.has(interest.id);
                          return (
                            <TableRow key={interest.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{interest.name}</p>
                                  {interest.description && (
                                    <p
                                      className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate"
                                      title={interest.description}
                                    >
                                      {interest.description}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatAudience(interest)}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                                {interest.topic || '—'}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-xs">
                                <span className="truncate block" title={interest.path.join(' > ')}>
                                  {interest.path.join(' > ')}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => void handleGetSuggestions(interest)}
                                    disabled={suggestionsLoading || searchLoading}
                                    title="Ver sugestões relacionadas"
                                  >
                                    {suggestionsLoading ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Lightbulb className="h-3.5 w-3.5" />
                                    )}
                                    <span className="sr-only">Sugestões</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={isSaved ? 'secondary' : 'outline'}
                                    onClick={() => !isSaved && void handleSaveInterest(interest)}
                                    disabled={isSaving || isSaved}
                                    title={isSaved ? 'Já salvo' : 'Salvar interesse'}
                                    className={cn(isSaved && 'text-green-600 dark:text-green-400')}
                                  >
                                    {isSaving ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : isSaved ? (
                                      <BookmarkCheck className="h-3.5 w-3.5" />
                                    ) : (
                                      <Bookmark className="h-3.5 w-3.5" />
                                    )}
                                    <span className="sr-only">
                                      {isSaved ? 'Salvo' : 'Salvar'}
                                    </span>
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Empty state when no search has been done yet */}
          {!searchResult && !searchLoading && !searchError && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Target className="h-12 w-12 mb-4 opacity-30" />
              <p className="text-base font-medium mb-1">Nenhuma pesquisa realizada</p>
              <p className="text-sm">Digite uma palavra-chave e clique em Pesquisar para começar.</p>
            </div>
          )}
        </TabsContent>

        {/* === SAVED INTERESTS TAB === */}
        <TabsContent value="saved" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Interesses Salvos</CardTitle>
                  <CardDescription>
                    Interesses marcados para uso em campanhas de anúncios
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void fetchSavedInterests(savedPage)}
                  disabled={savedLoading}
                >
                  {savedLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="sr-only">Atualizar</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {savedLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Carregando...
                </div>
              ) : savedInterests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Bookmark className="h-8 w-8 mb-3 opacity-40" />
                  <p className="text-sm">Nenhum interesse salvo ainda.</p>
                  <p className="text-xs mt-1">Pesquise interesses e clique em Salvar.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead className="text-right">
                            <span className="flex items-center justify-end gap-1">
                              <Users className="h-3.5 w-3.5" />
                              Audiência
                            </span>
                          </TableHead>
                          <TableHead className="hidden md:table-cell">Tópico</TableHead>
                          <TableHead className="hidden lg:table-cell">Palavra-chave</TableHead>
                          <TableHead className="hidden xl:table-cell">Notas</TableHead>
                          <TableHead className="hidden lg:table-cell">Salvo em</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {savedInterests.map(interest => {
                          const savedDate = interest.savedAt ?? interest.createdAt;
                          return (
                            <TableRow key={interest.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{interest.name}</p>
                                  {interest.path.length > 0 && (
                                    <p
                                      className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate"
                                      title={interest.path.join(' > ')}
                                    >
                                      {interest.path.join(' > ')}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatAudience(interest)}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                                {interest.topic || '—'}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                                {interest.keywordSearched}
                              </TableCell>
                              <TableCell className="hidden xl:table-cell text-sm text-muted-foreground max-w-xs">
                                <span
                                  className={cn(
                                    'truncate block cursor-pointer hover:text-foreground transition-colors',
                                    !interest.notes && 'italic opacity-60'
                                  )}
                                  title={interest.notes || 'Clique para adicionar notas'}
                                  onClick={() => openNotesDialog(interest)}
                                >
                                  {interest.notes || 'Sem notas'}
                                </span>
                              </TableCell>
                              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                                {savedDate ? formatDate(savedDate) : '—'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openNotesDialog(interest)}
                                    title="Editar notas"
                                  >
                                    <span className="text-xs">Notas</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => void handleDeleteSaved(interest.id)}
                                    disabled={deletingId === interest.id}
                                    className="text-destructive hover:text-destructive"
                                    title="Remover interesse salvo"
                                  >
                                    {deletingId === interest.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3.5 w-3.5" />
                                    )}
                                    <span className="sr-only">Excluir</span>
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {savedTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        {savedTotal} interesse{savedTotal !== 1 ? 's' : ''} salvo{savedTotal !== 1 ? 's' : ''}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void fetchSavedInterests(savedPage - 1)}
                          disabled={savedPage === 0 || savedLoading}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {savedPage + 1} / {savedTotalPages}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void fetchSavedInterests(savedPage + 1)}
                          disabled={savedPage + 1 >= savedTotalPages || savedLoading}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === HISTORY TAB === */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Histórico de Pesquisas</CardTitle>
              <CardDescription>
                Pesquisas realizadas anteriormente — clique para repetir
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  placeholder="Filtrar por palavra-chave..."
                  value={historySearchInput}
                  onChange={e => setHistorySearchInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setHistoryKeyword(historySearchInput);
                      void fetchHistory(0, historySearchInput);
                    }
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    setHistoryKeyword(historySearchInput);
                    void fetchHistory(0, historySearchInput);
                  }}
                  disabled={historyLoading}
                >
                  {historyLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="sr-only">Filtrar</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Carregando histórico...
                </div>
              ) : historyItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Clock className="h-8 w-8 mb-3 opacity-40" />
                  <p className="text-sm">
                    {historyKeyword ? `Nenhum resultado para "${historyKeyword}"` : 'Nenhum histórico de pesquisa.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Palavra-chave</TableHead>
                          <TableHead className="hidden md:table-cell">Tipo</TableHead>
                          <TableHead className="hidden md:table-cell text-right">Resultados</TableHead>
                          <TableHead className="hidden lg:table-cell">Pesquisado por</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Ação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyItems.map(item => (
                          <TableRow
                            key={item.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleRerunHistorySearch(item)}
                          >
                            <TableCell className="font-medium text-sm">
                              {item.keyword}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                              {item.searchType || 'Pesquisa'}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-right text-sm text-muted-foreground">
                              {item.resultCount !== undefined ? item.resultCount : '—'}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                              {item.searchedBy || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(item.searchedAt)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRerunHistorySearch(item);
                                }}
                                title="Repetir esta pesquisa"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                <span className="sr-only">Repetir pesquisa</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {historyTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        {historyTotal} pesquisa{historyTotal !== 1 ? 's' : ''}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void fetchHistory(historyPage - 1, historyKeyword)}
                          disabled={historyPage === 0 || historyLoading}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {historyPage + 1} / {historyTotalPages}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void fetchHistory(historyPage + 1, historyKeyword)}
                          disabled={historyPage + 1 >= historyTotalPages || historyLoading}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notes Edit Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Notas</DialogTitle>
            <DialogDescription>
              {editingInterest?.name} — adicione observações para uso interno
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="notes-textarea">Notas</Label>
            <Textarea
              id="notes-textarea"
              placeholder="Adicione observações sobre este interesse..."
              value={notesValue}
              onChange={e => setNotesValue(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                // Notes are saved locally for display — re-save would require a PATCH/PUT endpoint
                // Since the backend spec only shows POST (create) and DELETE, we update local state
                if (editingInterest) {
                  setSavedInterests(prev =>
                    prev.map(i =>
                      i.id === editingInterest.id ? { ...i, notes: notesValue } : i
                    )
                  );
                }
                setNotesDialogOpen(false);
                toast.success('Notas atualizadas localmente.');
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
